"use client";

import { excerptWithoutTitle } from "@/lib/blogArticle";

import { useState } from "react";
import Link from "next/link";
import { RevealGroup, RevealItem } from "@/components/Motion";
import type { MediaItem } from "@/lib/contentDb";

const PAGE_SIZE = 9;

function formatDate(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function PostCard({ item }: { item: MediaItem }) {
  const meta = [formatDate(item.date), item.source].filter(Boolean).join(" · ");
  const href = item.slug ? `/blog/${item.slug}` : item.url ?? null;
  const external = !item.slug && !!item.url;

  const inner = (
    <>
      {item.image && (
        <div className="overflow-hidden rounded-2xl ring-1 ring-brand-line">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            // 16:9, which is what the headers are actually authored at —
            // measured across all 79 published blog images: 72 at 1.78, 7 at
            // 2.77, nothing else. At 3:2 the whole image showed but floated
            // between grey bands; matching the dominant ratio fills the tile
            // and takes 35px off its height (224 -> 189) at the grid's 336px
            // column. brand-veil still backs the seven panoramic outliers,
            // which band slightly rather than crop.
            className="aspect-video w-full bg-brand-veil object-contain transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
      )}
      <div className="mt-5 flex items-baseline gap-3">
        <span className="mono-meta text-brand-accent-dark">Blog</span>
        {meta && <span className="mono-meta text-brand-muted">{meta}</span>}
      </div>
      <h3 className="t-title mt-3 text-brand-ink transition-colors group-hover:text-brand-accent">
        {item.title}
      </h3>
      {/* Many excerpts open with a line restating the headline, which would
          print the title twice on a card. */}
      {excerptWithoutTitle(item.excerpt, item.title) && (
        <p className="mt-3 max-w-xl text-brand-slate">{excerptWithoutTitle(item.excerpt, item.title)}</p>
      )}
      {href && (
        <p className="mt-4 text-sm font-medium text-brand-accent-dark transition-colors group-hover:text-brand-ink">
          Read{" "}
          <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
            &rarr;
          </span>
        </p>
      )}
    </>
  );

  const cardCls = "group flex h-full flex-col";
  if (!href) return <article className={cardCls}>{inner}</article>;
  return external ? (
    <a href={href} target="_blank" rel="noopener" className={cardCls}>
      {inner}
    </a>
  ) : (
    <Link href={href} className={cardCls}>
      {inner}
    </Link>
  );
}

// Paginated blog grid (9 per page). Newest-first order is set by the server
// (getMediaItems orders by date desc); this only slices into pages and
// scrolls back to the grid top on page change. Mirrors the newsroom pager.
export function BlogList({ items }: { items: MediaItem[] }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const goTo = (p: number) => {
    setPage(p);
    if (typeof window !== "undefined") {
      document.getElementById("blog-posts")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div id="blog-posts" className="scroll-mt-24">
      <RevealGroup
        key={current}
        className="mt-14 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3"
      >
        {paged.map((item) => (
          <RevealItem key={item.id} className="h-full">
            <PostCard item={item} />
          </RevealItem>
        ))}
      </RevealGroup>

      {totalPages > 1 && (
        <nav aria-label="Pagination" className="mt-16 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goTo(current - 1)}
            disabled={current === 1}
            aria-label="Previous page"
            className="rounded-full border border-brand-line px-4 py-2 text-sm font-medium text-brand-slate transition-colors hover:border-brand-accent hover:text-brand-accent-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-brand-line disabled:hover:text-brand-slate"
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => goTo(p)}
              aria-current={p === current ? "page" : undefined}
              className={`h-10 w-10 rounded-full text-sm font-medium transition-colors ${
                p === current
                  ? "bg-brand-accent text-brand-paper"
                  : "border border-brand-line text-brand-slate hover:border-brand-accent hover:text-brand-accent-dark"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => goTo(current + 1)}
            disabled={current === totalPages}
            aria-label="Next page"
            className="rounded-full border border-brand-line px-4 py-2 text-sm font-medium text-brand-slate transition-colors hover:border-brand-accent hover:text-brand-accent-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-brand-line disabled:hover:text-brand-slate"
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  );
}
